import { DatePicker } from '@mui/lab';
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import ChooseDate from 'shell/components/shared/choice-date';
import ListGender from 'shell/components/shared/gender';
import { memo, useCallback } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import AireList from 'system/components/shared/aire-list';
import Foreign from 'shell/components/shared/consult-person/steps/registry-office/generality/foreign';
import Italian from 'shell/components/shared/consult-person/steps/registry-office/generality/italian';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { FormikErrors, FormikTouched } from 'formik';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'shell/view-models/foreign-form';
import { DateTime } from 'luxon';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import RegistrationReason from 'shell/components/shared/registration-reason';
import ListFiscalCodeID from '../fiscal-code-id';

interface FormGeneralityProps {
  isEditMode?: boolean;
  disabled?: boolean;
  generality?: AccordionGeneralityForm;
  person?: AccordionPersonForm | null;
  errorsGenerality?: FormikErrors<AccordionGeneralityForm>;
  touchedGenerality?: FormikTouched<AccordionGeneralityForm>;
  errorsPerson?: FormikErrors<AccordionPersonForm>;
  touchedPerson?: FormikTouched<AccordionPersonForm>;
  onChangePerson: (
    key: keyof AccordionPersonForm,
    value: AccordionPersonForm[keyof AccordionPersonForm]
  ) => void;
  onChangeGenerality: (
    key: keyof AccordionGeneralityForm,
    value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
  ) => void;
  onChangeFieldsGenerality: (values: AccordionGeneralityForm) => void;
  onChangeFieldsPerson: (values: AccordionPersonForm) => void;
  disabledBirthDate?: boolean;
  labelMunicipalityItalian?: string;
  labelProvinceItalian?: string;
  ForPracticeTemporary?: boolean;
  requiredFiscalCode?: boolean;
  disabledValidityFiscalCode?: boolean;
}

const FormGeneralityComponent = ({
  isEditMode,
  disabled,
  person,
  generality,
  errorsPerson,
  touchedPerson,
  errorsGenerality,
  touchedGenerality,
  onChangeGenerality,
  onChangePerson,
  onChangeFieldsGenerality,
  ForPracticeTemporary,
  onChangeFieldsPerson,
  disabledBirthDate = false,
  labelMunicipalityItalian,
  labelProvinceItalian,
  requiredFiscalCode = false,
  disabledValidityFiscalCode = false,
}: FormGeneralityProps): React.ReactElement => {
  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChangeGenerality('italian', {
        ...(generality || {}).italian,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChangeGenerality('foreign', {
        ...(generality || {}).foreign,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const handleListGenderChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangeGenerality('gender', event.target.value);
  });

  const handleAireListChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangeGenerality('aire', event.target.value);
  });

  const handleChooseDateChange = useEventCallback(
    (eventDate: DateTime | null, choice?: Array<DatePickerView>) => {
      onChangeFieldsGenerality({
        birthDate: eventDate,
        birthChoiceDate: choice,
      });
    }
  );

  const onChangeListFiscalCodeID = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangeGenerality('validityFiscalCodeId', event.target.value);
  });

  return (
    <Grid container paddingTop={0} spacing={2}>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label={requiredFiscalCode ? 'Codice fiscale *' : 'Codice fiscale'}
          aria-label="Codice fiscale"
          value={person?.fiscalCode?.toUpperCase() || ''}
          onChange={(event) => {
            onChangePerson('fiscalCode', event.target.value);
          }}
          helperText={errorsPerson?.fiscalCode}
          error={touchedPerson?.fiscalCode && Boolean(errorsPerson?.fiscalCode)}
          fullWidth
          disabled={isEditMode ? false : disabled}
          inputProps={{ maxLength: '16' }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <ListFiscalCodeID
          disabled
          onChange={onChangeListFiscalCodeID}
          value={generality?.validityFiscalCodeId || ''}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Validità Codice fiscale"
          aria-label="Validità Codice fiscale"
          id="validity-fiscal-code"
          fullWidth
          value={generality?.validityFiscalCode || ''}
          onChange={(event) => {
            onChangeGenerality('validityFiscalCode', event.target.value);
          }}
          disabled={disabled || disabledValidityFiscalCode}
          inputProps={{ maxLength: '2' }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <DatePicker
          label="Data validità CF"
          aria-label="Data Validità Codice fiscale"
          value={generality?.validityDateFiscalCode || null}
          onChange={(date) => {
            onChangeGenerality('validityDateFiscalCode', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="validityDateFiscalCode"
              fullWidth
              helperText={errorsGenerality?.validityDateFiscalCode}
              error={
                touchedGenerality?.validityDateFiscalCode &&
                Boolean(errorsGenerality?.validityDateFiscalCode)
              }
            />
          )}
        />
      </Grid>
      <Grid item container xs={12} md={10} alignItems="center" columnSpacing={2}>
        <Grid item xs={12} md={9}>
          <PmiTextField
            label={person?.withoutSurname ? 'Cognome' : 'Cognome *'}
            aria-label="Cognome"
            id="surname"
            fullWidth
            disabled={isEditMode && !person?.withoutSurname ? false : disabled}
            helperText={errorsPerson?.surname}
            error={touchedPerson?.surname && Boolean(errorsPerson?.surname)}
            value={person?.surname || ''}
            inputProps={{
              maxLength: '80',
            }}
            onChange={(event) => {
              onChangePerson('surname', event.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutSurname-replica"
                checked={person?.withoutSurname || false}
                onChange={() => {
                  onChangeFieldsPerson({
                    withoutSurname: !person?.withoutSurname,
                    surname: !person?.withoutSurname ? '' : person?.surname,
                  });
                }}
                inputProps={{ 'aria-label': 'Persona senza cognome' }}
                disabled={isEditMode ? false : disabled}
              />
            }
            label="Persona senza cognome"
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} md={10} alignItems="center" columnSpacing={2}>
        <Grid item xs={12} md={9}>
          <PmiTextField
            label={person?.withoutName ? 'Nome' : 'Nome *'}
            aria-label="Nome"
            id="name"
            fullWidth
            disabled={isEditMode && !person?.withoutName ? false : disabled}
            inputProps={{
              maxLength: '80',
            }}
            helperText={errorsPerson?.name}
            error={touchedPerson?.name && Boolean(errorsPerson?.name)}
            value={person?.name || ''}
            onChange={(event) => {
              onChangePerson('name', event.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutName-replica"
                checked={person?.withoutName || false}
                onChange={() => {
                  onChangeFieldsPerson({
                    withoutName: !person?.withoutName,
                    name: !person?.withoutName ? '' : person?.name,
                  });
                }}
                inputProps={{ 'aria-label': 'Persona senza nome' }}
                disabled={isEditMode ? false : disabled}
              />
            }
            label="Persona senza nome"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item container columnSpacing={2}>
        <Grid item xs={12} md={4}>
          <ListGender
            required
            value={generality?.gender || ''}
            onChange={handleListGenderChange}
            disabled={isEditMode ? false : disabled}
            error={Boolean(errorsGenerality?.gender) && touchedGenerality?.gender}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <AireList
            value={generality?.aire || ''}
            onChange={handleAireListChange}
            disabled={isEditMode ? false : disabled}
            error={Boolean(errorsGenerality?.aire)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          {generality?.isForeign ? (
            <PmiTextField
              label="Anno Espatrio"
              id="expatriation-year"
              fullWidth
              disabled={isEditMode ? false : disabled}
              helperText={errorsGenerality?.expatriationYear}
              error={
                touchedGenerality?.expatriationYear && Boolean(errorsGenerality?.expatriationYear)
              }
              aria-label="Anno Espatrio"
              value={generality?.expatriationYear || ''}
              onChange={(event) => {
                onChangeGenerality(
                  'expatriationYear',
                  event.target.validity ? event.target.value : generality?.expatriationYear
                );
              }}
              inputProps={{ maxLength: '4' }}
            />
          ) : undefined}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} container alignItems="center">
        <ChooseDate
          required
          selected={generality?.birthDate || null}
          onChange={handleChooseDateChange}
          choice={generality?.birthChoiceDate}
          disabled={isEditMode ? false : disabled || disabledBirthDate}
          error={touchedGenerality?.birthDate && Boolean(errorsGenerality?.birthDate)}
          helperText={errorsGenerality?.birthDate}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Nascita"
            id="isForeign"
            value={generality?.isForeign || 'false'}
            onChange={() => {
              onChangeFieldsGenerality({
                isForeign: !generality?.isForeign,
                expatriationYear: '',
                italian: {
                  location: '',
                  province: '',
                  municipality: null,
                },
                foreign: {
                  state: null,
                  province: '',
                  municipality: null,
                },
              });
            }}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Nascita Italia"
              disabled={isEditMode ? false : disabled}
            />
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="Nascita Estero"
              disabled={isEditMode ? false : disabled}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!generality?.isForeign ? (
        <Italian
          disabledComponent={isEditMode ? false : disabled}
          values={generality?.italian}
          onChange={onFieldItalianChange}
          errors={errorsGenerality?.italian as FormikErrors<ItalianForm>}
          touched={touchedGenerality?.italian as unknown as FormikTouched<ItalianForm>}
          labelMunicipality={labelMunicipalityItalian}
          labelProvince={labelProvinceItalian}
          birthday={generality?.birthDate}
        />
      ) : (
        <Foreign
          disabledComponent={isEditMode ? false : disabled}
          values={generality?.foreign}
          onChange={onFieldForeignChange}
          errors={errorsGenerality?.foreign as FormikErrors<ForeignForm>}
          touched={touchedGenerality?.foreign as unknown as FormikTouched<ForeignForm>}
        />
      )}
      {isEditMode ||
      generality?.exceptionalBirthPlace ||
      generality?.municipalityIdentifier ||
      generality?.istatCode ||
      generality?.anprIdentifier ? (
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <PmiTextField
              aria-label="Luogo di nascita eccezionale"
              label="Luogo di nascita eccezionale"
              id="exceptionalBirthplace"
              name="exceptionalBirthplace"
              fullWidth
              disabled={isEditMode ? false : disabled}
              type="text"
              variant="outlined"
              inputProps={{
                maxLength: '120',
              }}
              helperText={errorsGenerality?.exceptionalBirthPlace}
              error={
                touchedGenerality?.exceptionalBirthPlace &&
                Boolean(errorsGenerality?.exceptionalBirthPlace)
              }
              value={generality?.exceptionalBirthPlace || ''}
              onChange={(event) => {
                onChangeGenerality('exceptionalBirthPlace', event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              aria-label="Identificativo del Comune"
              label="Identificativo del Comune"
              id="municipalityId"
              fullWidth
              disabled={disabled}
              helperText={errorsGenerality?.municipalityIdentifier}
              error={
                touchedGenerality?.municipalityIdentifier &&
                Boolean(errorsGenerality?.municipalityIdentifier)
              }
              type="text"
              variant="outlined"
              value={generality?.municipalityIdentifier || ''}
              onChange={(event) => {
                onChangeGenerality('municipalityIdentifier', event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              aria-label="Codice Istat"
              label="Codice Istat"
              id="istatCode"
              fullWidth
              disabled={disabled}
              type="text"
              variant="outlined"
              value={generality?.istatCode || ''}
              onChange={(event) => {
                onChangeGenerality('istatCode', event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              aria-label="Identificativo ANPR"
              label="Identificativo ANPR"
              id="anprId"
              name="anprId"
              fullWidth
              disabled={disabled}
              type="text"
              variant="outlined"
              value={generality?.anprIdentifier || ''}
              onChange={(event) => {
                onChangeGenerality('anprIdentifier', event.target.value);
              }}
            />
          </Grid>
        </>
      ) : undefined}
      {isEditMode || generality?.note ? (
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <PmiTextField
              aria-label="Note"
              label="Note"
              id="note"
              name="note"
              fullWidth
              disabled={isEditMode ? false : disabled}
              helperText={errorsGenerality?.note}
              error={touchedGenerality?.note && Boolean(errorsGenerality?.note)}
              type="text"
              variant="outlined"
              multiline
              rows={10}
              value={generality?.note || ''}
              onChange={(event) => {
                onChangeGenerality('note', event.target.value);
              }}
              inputProps={{
                maxLength: '240',
              }}
            />
          </Grid>
        </>
      ) : undefined}
      {ForPracticeTemporary ? (
        <Grid item xs={12}>
          <RegistrationReason
            label="Motivo registrazione"
            value={generality?.registrationReason || ''}
            required
            disabled={isEditMode ? false : disabled}
            onChange={(event) => onChangeGenerality('registrationReason', event.target.value)}
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

FormGeneralityComponent.displayName = 'Form generality component';

const FormGenerality = memo(FormGeneralityComponent);

export default FormGenerality;
